import { DialogComponent } from '@theme/dialog';
import { debounce, encodeObfuscatedUrl } from '@theme/utilities';
import {
  createDebug,
  createDynamicCard,
  hasDynamicCards,
  nostoSearch,
  PRODUCT_FIELDS,
  withNostoApi,
} from '@theme/nosto-search-engine';

/**
 * Search layer, powered by the Nosto search API.
 *
 * The header field is the real input: it lives outside the `<dialog>`, and
 * focusing it opens the layer. That only works because the dialog is **non
 * modal** (`get modal()` below): a modal one would move the focus into itself
 * and make the header inert, which would force a second, duplicated input. The
 * price is that the focus trap, the backdrop and the top layer are ours to
 * handle — see `#onFocusOut` and `#pinBelowHeader`.
 *
 * Three states live in the same `<dialog>`:
 *   - **idle**: manual suggestions, recent searches (local only) and the
 *     merchandised category tiles — all rendered by Liquid.
 *   - **typing** (from `MIN_QUERY_LENGTH` characters, debounced): Nosto keyword
 *     suggestions plus a short product preview, tracked as `autocomplete`.
 *   - **results**: the full list, tracked as `serp`, extended by "load more".
 *
 * Products are always rendered by `<nosto-dynamic-card>`, i.e. by our own
 * `product-card` section through the Section Rendering API: one single rendering
 * path, no parallel light-card template to keep in sync.
 *
 * Whatever fails — Nosto absent, account not live, timeout, Web Components
 * missing, layer disabled in the theme settings — a submit falls back to a plain
 * navigation to `/search`, which is server-rendered and always answers. The
 * no-JS path is the form's own `action`.
 *
 * Debug: append `?nostodebug=true` to log every query and every skipped run.
 */

/** Below this many characters we do not query Nosto at all. */
const MIN_QUERY_LENGTH = 3;

/** Keystroke debounce before the preview request. */
const INPUT_DEBOUNCE = 300;

const PREVIEW_PRODUCTS = 4;
const PREVIEW_KEYWORDS = 5;

/** First screen and "load more" increment of the full results list. */
const RESULTS_PAGE_SIZE = 12;

// Not `nosto_cm_timeout`: that 500ms budget suits listings that keep a native grid to fall back on.
// Here a timeout leaves the layer empty while Nosto's answer still lands; tail latency was measured
// above 1.2s on fibre.
const DEFAULT_TIMEOUT = 2000;

const HISTORY_KEY = 'afibel:search-history';
const HISTORY_LIMIT = 5;

const debug = createDebug('search-layer');

/**
 * Recent searches are kept on the device only: they are never sent to the server
 * nor to Nosto.
 * @returns {string[]}
 */
function readHistory() {
  try {
    const parsed = JSON.parse(window.localStorage.getItem(HISTORY_KEY) ?? '[]');
    if (!Array.isArray(parsed)) return [];
    return parsed.filter((entry) => typeof entry === 'string' && entry.trim()).slice(0, HISTORY_LIMIT);
  } catch {
    // Private mode, quota, corrupted value: the feature simply disappears.
    return [];
  }
}

/** @param {string[]} entries */
function writeHistory(entries) {
  try {
    window.localStorage.setItem(HISTORY_KEY, JSON.stringify(entries.slice(0, HISTORY_LIMIT)));
  } catch {
    // Nothing to do: an unavailable storage only costs the history.
  }
}

/**
 * Most recent first, case-insensitive de-duplication.
 * @param {string} terms
 * @returns {string[]}
 */
function rememberSearch(terms) {
  const value = terms.trim();
  if (!value) return readHistory();

  const previous = readHistory().filter((entry) => entry.toLowerCase() !== value.toLowerCase());
  const next = [value, ...previous].slice(0, HISTORY_LIMIT);
  writeHistory(next);
  return next;
}

/**
 * @param {string} terms
 * @returns {string[]}
 */
function forgetSearch(terms) {
  const next = readHistory().filter((entry) => entry.toLowerCase() !== terms.toLowerCase());
  writeHistory(next);
  return next;
}

/**
 * Nosto returns keyword suggestions in a shape we have not exercised against a
 * live index yet, so every plausible form is accepted.
 * @param {NostoSearchResult} result
 * @returns {string[]}
 */
function readKeywords(result) {
  const raw = /** @type {any} */ (result.keywords);
  /** @type {any[]} */
  const list = Array.isArray(raw) ? raw : Array.isArray(raw?.hits) ? raw.hits : [];

  return list
    .map((entry) => (typeof entry === 'string' ? entry : (entry?.keyword ?? entry?.value ?? '')))
    .map((keyword) => String(keyword).trim())
    .filter(Boolean);
}

/**
 * @typedef {Object} SearchDialogRefs
 * @property {HTMLDialogElement} dialog
 * @property {HTMLInputElement} input
 * @property {HTMLButtonElement} clearButton
 * @property {HTMLElement} scrollArea
 * @property {HTMLElement} idleState
 * @property {HTMLElement} typingState
 * @property {HTMLElement} resultsState
 * @property {HTMLElement} historySection
 * @property {HTMLElement} historyList
 * @property {HTMLElement} keywords
 * @property {HTMLElement} previewGrid
 * @property {HTMLElement} previewCta
 * @property {HTMLElement} resultsGrid
 * @property {HTMLButtonElement} loadMore
 * @property {HTMLElement} feedback
 * @property {HTMLElement} status
 */

/** @extends {DialogComponent} */
class SearchDialog extends DialogComponent {
  /** @type {SearchDialogRefs} */
  refs = /** @type {SearchDialogRefs} */ ({});

  requiredRefs = [
    'dialog',
    'input',
    'clearButton',
    'scrollArea',
    'idleState',
    'typingState',
    'resultsState',
    'historySection',
    'historyList',
    'keywords',
    'previewGrid',
    'previewCta',
    'resultsGrid',
    'loadMore',
    'feedback',
    'status',
  ];

  #abortController = new AbortController();

  /** Guards against a stale response landing after a newer interaction. */
  #generation = 0;

  /** Terms of the results currently displayed, for "load more". */
  #query = '';

  /** Offset of the next "load more" page. */
  #from = 0;

  #total = 0;

  /** Hits currently on screen, keyed by product id, for the click beacon. */
  #shown = /** @type {Map<string, { hit: NostoProductHit, track: string }>} */ (new Map());

  #handleInput = debounce(() => this.#preview(), INPUT_DEBOUNCE);

  /** Set while closing so the focus we hand back does not reopen the layer. */
  #suppressReopen = false;

  #placeholderQuery = window.matchMedia('(min-width: 990px)');

  get modal() {
    return false;
  }

  connectedCallback() {
    super.connectedCallback();

    if (this.#abortController.signal.aborted) {
      this.#abortController = new AbortController();
    }

    const { signal } = this.#abortController;

    this.refs.input.addEventListener('input', this.#onInput, { signal });
    this.refs.input.addEventListener('focus', this.#onFieldFocus, { signal });
    this.addEventListener('focusout', this.#onFocusOut, { signal });
    this.addEventListener('submit', this.#onSubmit, { signal });
    this.addEventListener('click', this.#onClick, { signal });
    this.addEventListener('dialog:open', this.#onOpen, { signal });
    this.addEventListener('dialog:close', this.#onClose, { signal });

    this.#placeholderQuery.addEventListener('change', this.#syncPlaceholder, { signal });

    this.#renderHistory(readHistory());
    this.#syncClearButton();
    this.#syncPlaceholder();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#handleInput.cancel();
    this.#abortController.abort();
  }

  /* ---------------------------------------------------------------------------
   * Interactions
   * ------------------------------------------------------------------------- */

  #onInput = () => {
    this.#syncClearButton();
    // Escape and the close button hand the focus back to the field, so no further `focus` event
    // will ever fire on it: typing is the only signal left that the visitor wants the layer back.
    // A no-op while it is already open.
    this.showDialog();
    this.#handleInput();
  };

  /**
   * Tabbing into the field opens the layer. The focus we hand back on close is
   * indistinguishable from it, hence the guard; a deliberate click goes through
   * `#onClick` instead, which the guard does not cover.
   */
  #onFieldFocus = () => {
    if (this.#suppressReopen) return;
    this.showDialog();
  };

  /**
   * Nothing keeps the focus inside a non-modal dialog, so a focus leaving the
   * component closes the layer. `relatedTarget` is null when the focus simply
   * drops (a click on a non-focusable area of the layer): the outside click
   * handler already covers that case, so we leave it alone.
   * @param {FocusEvent} event
   */
  #onFocusOut = (event) => {
    if (!this.refs.dialog.open) return;

    const next = event.relatedTarget;
    if (!(next instanceof Node) || this.contains(next)) return;

    this.closeDialog();
  };

  /** @param {Event} event */
  #onSubmit = (event) => {
    if (!(event.target instanceof HTMLFormElement)) return;

    // With the layer disabled we let the browser submit the form: the visitor
    // lands on the server-rendered results page.
    if (!this.nostoEnabled) return;

    event.preventDefault();
    this.#submit(this.#currentQuery);
  };

  /** @param {MouseEvent} event */
  #onClick = (event) => {
    if (!(event.target instanceof Element)) return;

    // Clicking the field opens the layer — including when the field already has
    // the focus, which fires no `focus` event and so would leave a just-closed
    // layer shut. The buttons it holds do their own thing.
    if (event.target.closest('.header-search__field') && !event.target.closest('button')) {
      this.refs.input.focus({ preventScroll: true });
      this.showDialog();
      return;
    }

    const removeButton = event.target.closest('[data-search-history-remove]');
    if (removeButton instanceof HTMLElement) {
      event.preventDefault();
      this.#renderHistory(forgetSearch(removeButton.dataset.searchHistoryRemove ?? ''));
      this.refs.input.focus({ preventScroll: true });
      return;
    }

    const fillButton = event.target.closest('[data-search-fill]');
    if (fillButton instanceof HTMLElement) {
      const value = fillButton.dataset.searchFill?.trim();
      if (!value) return;
      event.preventDefault();
      // A keyword comes from Nosto's own suggestions, which it wants to tell
      // apart from a typed query in its analytics.
      this.#submit(value, { isKeyword: fillButton.hasAttribute('data-search-keyword') });
      return;
    }

    if (event.target.closest('[ref="clearButton"]')) {
      event.preventDefault();
      this.#handleInput.cancel();
      this.#reset();
      this.refs.input.focus({ preventScroll: true });
      return;
    }

    if (event.target.closest('[ref="loadMore"]')) {
      event.preventDefault();
      this.#loadMore();
      return;
    }

    this.#recordClick(event.target);
  };

  #onOpen = () => {
    // `show()` runs the dialog focusing steps and takes the focus off the
    // field. Taking it back synchronously keeps the caret — and the software
    // keyboard — where the visitor put it.
    this.refs.input.focus({ preventScroll: true });

    this.refs.input.setAttribute('aria-expanded', 'true');
    this.#pinBelowHeader();
    this.#renderHistory(readHistory());
    this.#syncClearButton();

    requestAnimationFrame(() => {
      if (this.refs.input.value.trim()) {
        this.#preview();
      } else {
        this.#showIdle();
      }
    });
  };

  #onClose = () => {
    // Only take the focus back when it is still ours to give: a click outside
    // has already moved it where the visitor wanted it.
    const restoreFocus = this.contains(document.activeElement);

    this.#suppressReopen = true;
    this.refs.input.setAttribute('aria-expanded', 'false');
    this.#reset();

    if (restoreFocus) this.refs.input.focus({ preventScroll: true });
    requestAnimationFrame(() => {
      this.#suppressReopen = false;
    });
  };

  /**
   * The layer hangs off the bottom edge of the header, which `--header-height`
   * alone cannot give: the header is only pinned to the top of the viewport
   * once the announcement bar has scrolled away. Measuring on open is enough,
   * since the scroll lock then freezes the header for as long as it is open.
   */
  #pinBelowHeader() {
    const header = this.closest('.header-section');
    if (!(header instanceof HTMLElement)) return;

    this.style.setProperty('--search-layer-top', `${Math.max(Math.ceil(header.getBoundingClientRect().bottom), 0)}px`);
  }

  /** Liquid cannot pick between the two placeholder settings, the viewport can. */
  #syncPlaceholder = () => {
    const placeholder = this.#placeholderQuery.matches
      ? this.dataset.placeholderDesktop
      : this.dataset.placeholderMobile;

    if (placeholder) this.refs.input.placeholder = placeholder;
  };

  /* ---------------------------------------------------------------------------
   * Typing — keyword suggestions + short product preview
   * ------------------------------------------------------------------------- */

  async #preview() {
    const query = this.#currentQuery;

    if (!this.nostoEnabled || query.length < MIN_QUERY_LENGTH) {
      this.#showIdle();
      return;
    }

    const generation = ++this.#generation;
    this.#showState('typing');
    this.#setBusy(true);

    /** @type {NostoSearchResult} */
    let result;
    try {
      result = await nostoSearch(
        {
          query,
          products: { fields: PRODUCT_FIELDS, size: PREVIEW_PRODUCTS },
          keywords: { fields: ['keyword'], size: PREVIEW_KEYWORDS },
        },
        this.#timeout,
        { track: 'autocomplete', redirect: false },
      );
    } catch (error) {
      debug('preview failed', error);
      if (generation === this.#generation) this.#showIdle();
      return;
    }

    if (generation !== this.#generation) return;

    debug('preview response', result);
    this.#renderPreview(query, result);
  }

  /**
   * @param {string} query
   * @param {NostoSearchResult} result
   */
  #renderPreview(query, result) {
    const products = result.products ?? result;
    const hits = products.hits ?? [];
    const total = products.total ?? hits.length;

    this.#setBusy(false);
    this.#shown.clear();

    const keywords = readKeywords(result);
    this.refs.keywords.replaceChildren(...keywords.map((keyword) => this.#keywordItem(keyword)));
    this.refs.keywords.hidden = keywords.length === 0;

    const cards = hits.map((hit) => this.#cardItem(hit, 'autocomplete')).filter(Boolean);
    this.refs.previewGrid.replaceChildren(.../** @type {HTMLElement[]} */ (cards));
    this.refs.previewGrid.hidden = cards.length === 0;

    const hasResults = total > 0;
    this.refs.previewCta.hidden = !hasResults;
    this.refs.previewCta.textContent = this.#format(this.#plural('viewProductsTemplate', total), { count: `${total}` });
    this.refs.previewCta.dataset.o = encodeObfuscatedUrl(this.#searchPageUrl(query).toString());

    if (!hasResults && !keywords.length) {
      this.#say(this.#noResultsMessage(query), { visible: true });
    } else {
      this.refs.feedback.hidden = true;
      this.#say(this.#format(this.#plural('resultsTemplate', total), { count: `${total}`, terms: query }));
    }
  }

  /* ---------------------------------------------------------------------------
   * Submit — full results list
   * ------------------------------------------------------------------------- */

  /**
   * @param {string} terms
   * @param {{ isKeyword?: boolean }} [options]
   */
  async #submit(terms, { isKeyword = false } = {}) {
    const query = terms.trim();
    if (!query) return;

    this.#handleInput.cancel();
    this.refs.input.value = query;
    this.#syncClearButton();
    this.#renderHistory(rememberSearch(query));

    if (!this.nostoEnabled) {
      this.#leaveToSearchPage(query);
      return;
    }

    this.#query = query;
    this.#from = 0;
    this.#total = 0;

    const generation = ++this.#generation;
    // A newer run supersedes this one, and so does the visitor dismissing the layer: both navigate
    // on their own terms, and neither wants this run's answer. `dialog:close` only fires once the
    // closing animation is over, well after the visitor asked for the layer to go away, so the
    // class the close sets straight away is what we read instead of the generation alone.
    const isCurrent = () =>
      generation === this.#generation && !this.refs.dialog.classList.contains('dialog-closing');

    withNostoApi((api) => api.recordSearchSubmit?.(query));

    this.#showState('results');
    this.refs.resultsGrid.replaceChildren();
    this.refs.loadMore.hidden = true;
    this.#setBusy(true);
    this.#say(this.loadingText);

    /** @type {NostoSearchResult} */
    let result;
    try {
      result = await nostoSearch(
        { query, products: { fields: PRODUCT_FIELDS, size: RESULTS_PAGE_SIZE, from: 0 } },
        this.#timeout,
        { track: 'serp', redirect: true, isKeyword },
      );
    } catch (error) {
      debug('search failed — falling back to the results page', error);
      if (isCurrent()) this.#leaveToSearchPage(query);
      return;
    }

    if (!isCurrent()) return;

    // A keyword mapped to a landing page in the Nosto dashboard.
    const redirect = result.redirect ?? result.response?.redirect;
    if (redirect) {
      debug('following the Nosto redirect', redirect);
      window.location.assign(redirect);
      return;
    }

    const products = result.products ?? result;
    const hits = products.hits ?? [];
    debug('search response', products);

    if (!hits.length) {
      this.#setBusy(false);
      this.#shown.clear();
      this.#say(this.#noResultsMessage(query), { visible: true });
      return;
    }

    // Without the Web Components there is no way to render a card here, so the
    // server-rendered page takes over rather than showing an empty layer.
    if (!hasDynamicCards()) {
      debug('`nosto-dynamic-card` is not defined — falling back to the results page');
      this.#leaveToSearchPage(query);
      return;
    }

    this.#total = products.total ?? hits.length;
    this.#appendResults(hits);
    this.#say(this.#format(this.#plural('resultsTemplate', this.#total), { count: `${this.#total}`, terms: query }));
  }

  async #loadMore() {
    if (!this.#query) return;

    const generation = ++this.#generation;
    this.refs.loadMore.setAttribute('aria-busy', 'true');

    /** @type {NostoSearchResult} */
    let result;
    try {
      result = await nostoSearch(
        { query: this.#query, products: { fields: PRODUCT_FIELDS, size: RESULTS_PAGE_SIZE, from: this.#from } },
        this.#timeout,
        { track: 'serp', redirect: false },
      );
    } catch (error) {
      debug('load more failed', error);
      this.refs.loadMore.removeAttribute('aria-busy');
      return;
    }

    if (generation !== this.#generation) return;

    this.refs.loadMore.removeAttribute('aria-busy');

    const products = result.products ?? result;
    const hits = products.hits ?? [];
    if (!hits.length) {
      this.refs.loadMore.hidden = true;
      return;
    }

    this.#appendResults(hits);
  }

  /**
   * @param {NostoProductHit[]} hits
   */
  #appendResults(hits) {
    const cards = hits.map((hit) => this.#cardItem(hit, 'serp')).filter(Boolean);
    this.refs.resultsGrid.append(.../** @type {HTMLElement[]} */ (cards));

    this.#setBusy(false);
    this.#from += hits.length;
    this.refs.loadMore.hidden = this.#from >= this.#total;
    this.refs.feedback.hidden = true;
  }

  /* ---------------------------------------------------------------------------
   * Rendering helpers
   * ------------------------------------------------------------------------- */

  /**
   * @param {NostoProductHit} hit
   * @param {string} track
   * @returns {HTMLElement | null}
   */
  #cardItem(hit, track) {
    const item = document.createElement('li');
    item.className = 'header-search__card';

    const card = createDynamicCard(hit, {
      sizes: this.dataset.cardSizes ?? '',
      onEmpty: () => {
        debug(`dynamic card "${hit.url}" came back empty — slot dropped`);
        item.remove();
      },
    });

    if (!card) return null;

    item.appendChild(card);
    if (hit.productId) this.#shown.set(String(hit.productId), { hit, track });

    return item;
  }

  /**
   * @param {string} keyword
   * @returns {HTMLElement}
   */
  #keywordItem(keyword) {
    const item = document.createElement('li');
    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'header-search__pill';
    button.dataset.searchFill = keyword;
    button.setAttribute('data-search-keyword', '');
    button.textContent = keyword;
    item.appendChild(button);
    return item;
  }

  /**
   * Recent searches are rendered client-side: they only exist on this device.
   * @param {string[]} entries
   */
  #renderHistory(entries) {
    this.refs.historyList.replaceChildren(
      ...entries.map((terms) => {
        const item = document.createElement('li');
        item.className = 'header-search__pill header-search__pill--removable';

        const fill = document.createElement('button');
        fill.type = 'button';
        fill.className = 'button-unstyled header-search__pill-label';
        fill.dataset.searchFill = terms;
        fill.textContent = terms;

        const remove = document.createElement('button');
        remove.type = 'button';
        remove.className = 'button-unstyled header-search__pill-remove';
        remove.dataset.searchHistoryRemove = terms;
        remove.setAttribute('aria-label', this.#format(this.removeRecentTemplate, { terms }));
        remove.innerHTML = '<span aria-hidden="true">&times;</span>';

        item.append(fill, remove);
        return item;
      }),
    );

    this.refs.historySection.hidden = entries.length === 0;
  }

  /** @param {'idle' | 'typing' | 'results'} state */
  #showState(state) {
    this.refs.idleState.hidden = state !== 'idle';
    this.refs.typingState.hidden = state !== 'typing';
    this.refs.resultsState.hidden = state !== 'results';
    this.refs.feedback.hidden = true;
    this.refs.scrollArea.scrollTop = 0;
  }

  #showIdle() {
    this.#generation += 1;
    this.#query = '';
    this.#shown.clear();
    this.#showState('idle');
    this.#setBusy(false);
    this.refs.status.textContent = '';
  }

  #reset() {
    this.refs.input.value = '';
    this.#syncClearButton();
    this.#showIdle();
  }

  /**
   * Reports a click on a result, which feeds Nosto's click ranking. Fire and
   * forget: delaying the navigation to confirm the beacon would be worse.
   * @param {Element} target
   */
  #recordClick(target) {
    if (!this.#shown.size) return;

    const productId = target.closest('product-card[data-product-id]')?.getAttribute('data-product-id');
    if (!productId) return;

    const entry = this.#shown.get(productId);
    if (!entry) return;

    withNostoApi((api) => api.recordSearchClick?.(entry.track, { productId, url: entry.hit.url ?? '' }));
  }

  #leaveToSearchPage(/** @type {string} */ query) {
    window.location.assign(this.#searchPageUrl(query).toString());
  }

  /** @param {string} query */
  #searchPageUrl(query) {
    const url = new URL(this.searchUrl, window.location.origin);
    url.searchParams.set('q', query);
    url.searchParams.set('type', 'product');
    url.searchParams.set('options[prefix]', 'last');
    return url;
  }

  /** @param {boolean} isBusy */
  #setBusy(isBusy) {
    this.refs.scrollArea.setAttribute('aria-busy', isBusy ? 'true' : 'false');
  }

  /**
   * @param {string} message
   * @param {{ visible?: boolean }} [options] Also show the message in the layer,
   * not only to screen readers.
   */
  #say(message, { visible = false } = {}) {
    this.refs.status.textContent = message;
    if (!visible) return;
    this.refs.feedback.textContent = message;
    this.refs.feedback.hidden = false;
  }

  #syncClearButton() {
    this.refs.clearButton.hidden = this.refs.input.value.length === 0;
  }

  get #currentQuery() {
    return this.refs.input.value.trim();
  }

  get #timeout() {
    return Number(this.dataset.timeout) || DEFAULT_TIMEOUT;
  }

  get nostoEnabled() {
    return this.dataset.nostoEnabled === 'true';
  }

  get searchUrl() {
    return this.dataset.searchUrl ?? '/search';
  }

  get loadingText() {
    return this.dataset.loadingText ?? 'Loading…';
  }

  /**
   * Liquid cannot resolve a plural from the `__COUNT__` placeholder, so it renders
   * both forms and the component picks once it knows the real count.
   * @param {'resultsTemplate' | 'viewProductsTemplate'} name
   * @param {number} count
   */
  #plural(name, count) {
    const singular = this.dataset[`${name}One`];
    return (count === 1 && singular) || this.dataset[name] || '';
  }

  /**
   * "No results for X" plus the hint line; the newline is rendered by `white-space: pre-line`
   * on the feedback paragraph and is read as a pause by screen readers.
   * @param {string} query
   */
  #noResultsMessage(query) {
    return `${this.#format(this.noResultsTemplate, { terms: query })}\n${this.dataset.noResultsHint ?? ''}`.trim();
  }

  get noResultsTemplate() {
    return this.dataset.noResultsTemplate ?? 'No results for __TERMS__';
  }

  get removeRecentTemplate() {
    return this.dataset.removeRecentTemplate ?? 'Remove __TERMS__';
  }

  /**
   * @param {string} template
   * @param {{ count?: string, terms?: string }} replacements
   */
  #format(template, replacements) {
    return template.replace('__COUNT__', replacements.count ?? '').replace('__TERMS__', replacements.terms ?? '');
  }
}

if (!customElements.get('search-dialog')) {
  customElements.define('search-dialog', SearchDialog);
}
